import classNames from 'classnames/bind';
import { ReactNode, RefObject, useEffect, useRef } from 'react';

import { Exit } from '../../assets/icons/Icons';
import useClickOutside from '../../hooks/useClickOutside/useClickOutside';
import Styles from './FlyoutModal.module.scss';

const cx = classNames.bind(Styles);

interface FlyoutModalProps {
  noAnimation?: boolean;
  /** Dictates weather flyout modal is open or closed */
  show?: boolean;
  /** Children to render */
  children?: ReactNode;
  /** Call back when modal is closed */
  onClose: () => void;
}

interface ModalMethodProps {
  /** Children to render in header | body | footer */
  children?: ReactNode;
}

const FlyoutModal = ({ noAnimation, show, children, onClose }: FlyoutModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const initialLoad = useRef(true);
  const clickOutsideRef = useClickOutside(onClose, show) as RefObject<HTMLDivElement>;

  // Open or close modal when show changes
  useEffect(() => {
    // Prevent modal from doing closing animation on initial load
    if (initialLoad.current) {
      initialLoad.current = false;
    } else {
      show ? handleOpen() : handleClose();
    }
  }, [show, initialLoad.current]);

  // Handles the native Escape key closing of the dialog
  useEffect(() => {
    const dialogElement = dialogRef.current;

    const listener = (event: Event) => {
      event.preventDefault();
      onClose();
    };

    if (dialogElement) {
      dialogElement.addEventListener('cancel', listener);

      return () => {
        dialogElement.removeEventListener('cancel', listener);
      };
    }
    return undefined;
  }, []);

  const handleClose = () => {
    if (dialogRef.current) {
      dialogRef.current.setAttribute('closing', ''); // for styling purposes

      if (noAnimation) {
        dialogRef.current.close();
        return;
      }

      // Change dialog open state when animation is done
      dialogRef.current.addEventListener(
        'animationend',
        () => {
          dialogRef.current?.removeAttribute('closing');
          dialogRef.current?.close();
        },
        { once: true },
      );
    }
  };

  const handleOpen = () => {
    if (dialogRef.current?.hasAttribute('open')) {
      return; // already open
    }
    dialogRef.current?.showModal(); // need to use showModal for backdrop to show
  };

  return (
    <dialog ref={dialogRef} className={cx('modal', noAnimation ? 'noanimation' : '')}>
      <div className={cx('container')} ref={clickOutsideRef}>
        <div className={cx('header')}>
          <button className={cx('close')} onClick={onClose}>
            <span>Close</span> {Exit}
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
};

const HeaderComponent = ({ children }: ModalMethodProps) => <div className={cx('section-header')}>{children}</div>;
const BodyComponent = ({ children }: ModalMethodProps) => (
  <div className={cx('section-body')}>
    <div className={cx('body')}>{children}</div>
  </div>
);
const FooterComponent = ({ children }: ModalMethodProps) => (
  <div className={cx('section-footer')}>
    <div className={cx('footer')}>{children}</div>
  </div>
);
HeaderComponent.displayName = 'FlyoutModal.Header';
BodyComponent.displayName = 'FlyoutModal.Body';
FooterComponent.displayName = 'FlyoutModal.Header';

// Use for main header of modal
FlyoutModal.Header = HeaderComponent;
// Use for main body of modal
FlyoutModal.Body = BodyComponent;
// Use for footer of modal
// This component adds a divider between the body and footer
FlyoutModal.Footer = FooterComponent;

export default FlyoutModal;
